import { format } from "date-fns";
import {
  Eye,
  File,
  FileText
} from "lucide-react";

import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday.js";
import isYesterday from "dayjs/plugin/isYesterday.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(customParseFormat);


export function titleCase(str: string) {
  if(!str) return null
  const transformed = str.split("_").join(" ");
  return transformed
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");
}

export function commaSeparatedTitleCase(str=""){
  const transformedArray = str.split(",")
  return transformedArray.map(word => titleCase(word)).join(", ")
}


export function transformColumns(columns) {
  return columns.map((col) => ({
    ...col,
    headerName: titleCase(col.headerName ?? ""),
  }));
}

export const formatDateForDisplay = (date: string) => {
  const formattedDate = new Date(date).toISOString().split("T")[0];
  return formattedDate;
};

export const formatDate = (dateString: string) => {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNameInitials = (fullName:string, firstName?:string, lastName?:string) => {
  const full_name = fullName
    ? fullName.toUpperCase()
    : (firstName + " " + lastName).toUpperCase();
  const nameArr = full_name.split(" ");
  if (nameArr.length < 2) return full_name.charAt(0);
  const last_name = nameArr.pop();
  const first_name = nameArr[0];
  const full_initials = first_name.charAt(0) + last_name.charAt(0);
  return full_initials;
};

export const getDisplayAvatarChar = (user) => {
  if (!user || typeof user !== "object") return "?";
  if (user.role === "recruiter" && user.company_name)
    return getNameInitials(user.company_name);
  if (user.role === "admin") {
    if (user.full_name) return getNameInitials(user.full_name);
    if (user.first_name && user.last_name)
      return getNameInitials(user.full_name, user.first_name, user.last_name);
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    return "A";
  }
  if (user.full_name) return getNameInitials(user.full_name);
  if (user.first_name || user.last_name)
    return getNameInitials(user.full_name, user.first_name, user.last_name);
  return user.email.charAt(0).toUpperCase();
};

export function formatDateVerbose(dateString: string) {
  if (!dateString) return "";

  let date;

  const inputStr = String(dateString);

  if (inputStr.length === 14) {
    // Handle YYYYMMDDHHMMSS format
    const year = parseInt(inputStr.substring(0, 4));
    const month = parseInt(inputStr.substring(4, 6)) - 1; // Months are 0-indexed
    const day = parseInt(inputStr.substring(6, 8));
    const hour = parseInt(inputStr.substring(8, 10));
    const minute = parseInt(inputStr.substring(10, 12));
    const second = parseInt(inputStr.substring(12, 14));

    date = new Date(year, month, day, hour, minute, second);
  } else {
    // Try parsing as-is for other formats
    date = new Date(inputStr);
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const datePart = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const timePart = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/AM|PM/g, (match) => (match === "AM" ? "a.m." : "p.m."));

  return `${datePart}, ${timePart}`;
}

export const timeSince = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const seconds = Math.floor((new Date() - date) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return `${Math.floor(interval)} years ago`;
        interval = seconds / 2592000;
        if (interval > 1) return `${Math.floor(interval)} months ago`;
        interval = seconds / 86400;
        if (interval > 1) return `${Math.floor(interval)} days ago`;
        interval = seconds / 3600;
        if (interval > 1) return `${Math.floor(interval)} hours ago`;
        interval = seconds / 60;
        if (interval > 1) return `${Math.floor(interval)} minutes ago`;
        return `${Math.floor(seconds)} seconds ago`;
    };

    export const formatISOToDate = (date: string) => {
      if (!date) return null;
      return new Date(date)
    }

    export const formatDateToYMD = (date: string) => {
      if(!date) return null;
      return format(date, "yyyy-MM-dd")
    }

export const getFullName = (firstName = "", lastName = "") => {
  if (!firstName && !lastName) return "Not provided";
  return `${firstName} ${lastName}`;
};

// ADMIN HELPER FUNCS
export const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(extension)) return FileText;
  if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) return Eye;
  return File;
};

export const getStatusColor = (status) => {
  const statusColors = {
    verified: "success",
    pending: "warning",
    rejected: "error",
    needs_resubmission: "info",
  };
  return statusColors[status] || "default";
};

export const getCategoryColor = (category) => {
  const categoryColors = {
    cv: "primary",
    aviation_licenses: "secondary",
    education_certificates: "success",
    employment_certificates: "info",
    id_document: "warning",
    passport: "error",
    drivers_license: "primary",
    professional_certifications: "secondary",
    medical_certificates: "success",
    academic_transcripts: "info",
    work_experience: "warning",
  };
  return categoryColors[category] || "default";
};



export const getActionLabel = (action: string) => {
  switch (action) {
    case "verify":
      return "Verify";
    case "reject":
      return "Reject";
    case "needs_resubmission":
      return "Request Resubmission";
    default:
      return action;
  }
};

export const getDialogTitle = (action: string) => {
  switch (action) {
    case "verify":
      return "Verify Document";
    case "reject":
      return "Reject Document";
    case "needs_resubmission":
      return "Request Document Resubmission";
    default:
      return "Document Action";
  }
};


export const convertStringToOptions = (valueString: string, options: {label: string, value: string}[]) => {
  if (!valueString) return [];

  const values = Array.isArray(valueString) ? valueString : valueString.split(",").map(v => v.trim());

  return values
    .map((val) => options.find((opt) => opt.value === val))
    .filter(Boolean); // remove any nulls
};



export function normalizeOptionValue(fieldValue, options, isMulti = false) {
  if (!fieldValue) return isMulti ? [] : null;

  // Multi-select (comma-separated)
  if (isMulti) {
    const values = fieldValue.split(",").map(v => v.trim());
    return values
      .map(v => options.find(opt => opt.value === v))
      .filter(Boolean);
  }

  // Single select
  return options.find(opt => opt.value === fieldValue) || null;
}

export const formatMessageTime = (datetime: string) => {
  const date = dayjs(datetime, "YYYY-MM-DD HH:mm:ss");

  if (date.isToday()) {
    return date.format("h:mm A"); // 7:30 PM
  }

  if (date.isYesterday()) {
    return "Yesterday";
  }

  if (dayjs().diff(date, "day") < 7) {
    return date.format("dddd"); // Monday
  }

  return date.format("DD/MM/YYYY");
};

export const buildQueryPayload = (filters) => {
  return Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => {
      if (value === "" || value === null || value === undefined) return false;
      return true;
    })
  );
};

export const getFilenameFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    // Remove any query params e.g. ?AWSAccessKey=...
    return decodeURIComponent(filename.split('?')[0]);
  } catch {
    return null;
  }
};